/**
 * Shared anchor-scrolling helpers.
 *
 * The site header is `position: fixed`, so any scroll to a section needs an
 * offset or the heading ends up hidden behind the nav bar.
 */
export const HEADER_OFFSET = 88;

/** Smooth-scrolls to an element id, accounting for the fixed header. */
export function scrollToId(id: string, behavior: ScrollBehavior = "smooth") {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior });
  return true;
}

/**
 * Scrolls to a section AND writes the hash into the address bar so the URL can
 * be copied and shared (e.g. `/hospital-curbside-econsult#pricing`).
 */
export function scrollToSection(id: string) {
  const scrolled = scrollToId(id);
  if (scrolled && window.location.hash !== `#${id}`) {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${id}`);
  }
  return scrolled;
}

/**
 * Waits for a lazily-rendered element to exist, then scrolls to it.
 * Routes are code-split, so on a fresh page load the target section usually
 * isn't in the DOM yet when the hash is read.
 */
export function scrollToIdWhenReady(id: string, timeoutMs = 2500): () => void {
  let cancelled = false;
  let frame = 0;
  const start = performance.now();

  const tick = () => {
    if (cancelled) return;
    if (scrollToId(id)) return;
    if (performance.now() - start > timeoutMs) return;
    frame = requestAnimationFrame(tick);
  };

  frame = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}
